
import { supabase } from '@/integrations/supabase/client';
import { getCachedUserData } from './userCache';

// Cache for connection status
const CONNECTION_CACHE_KEY = 'etsy_connection_status';
const CONNECTION_CACHE_TIMESTAMP_KEY = 'etsy_connection_timestamp';

// Helper to check if cached connection data is still valid (5 minutes)
const isCachedConnectionValid = (): boolean => {
  const timestamp = localStorage.getItem(CONNECTION_CACHE_TIMESTAMP_KEY);
  if (!timestamp) return false;
  
  const cacheTime = new Date(timestamp).getTime();
  const now = new Date().getTime();
  const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  return (now - cacheTime) < fiveMinutes;
};

// Get cached connection status
const getCachedConnectionStatus = (): boolean | null => {
  const cached = localStorage.getItem(CONNECTION_CACHE_KEY);
  if (!cached || !isCachedConnectionValid()) return null;
  try {
    return JSON.parse(cached);
  } catch {
    return null;
  }
};

// Set cached connection status
const setCachedConnectionStatus = (isConnected: boolean) => {
  localStorage.setItem(CONNECTION_CACHE_KEY, JSON.stringify(isConnected));
  localStorage.setItem(CONNECTION_CACHE_TIMESTAMP_KEY, new Date().toISOString());
};

// Clear connection cache
export const clearConnectionCache = () => {
  localStorage.removeItem(CONNECTION_CACHE_KEY);
  localStorage.removeItem(CONNECTION_CACHE_TIMESTAMP_KEY);
};

export const checkEtsyConnection = async (hasActiveSubscription: boolean) => {
  if (!hasActiveSubscription) {
    setCachedConnectionStatus(false);
    return false;
  }

  // Check cache first
  const cachedStatus = getCachedConnectionStatus();
  if (cachedStatus !== null) {
    console.log('Using cached connection status:', cachedStatus);
    return cachedStatus;
  }

  try {
    const user = await getCachedUserData();
    if (!user) {
      setCachedConnectionStatus(false);
      return false;
    }

    const { data: shops } = await supabase
      .from('etsy_shops')
      .select('shop_name')
      .eq('user_id', user.id)
      .single();

    const isConnected = !!shops;
    
    console.log('Fetched and cached Etsy connection status:', isConnected);
    setCachedConnectionStatus(isConnected);
    return isConnected;
  } catch {
    setCachedConnectionStatus(false);
    return false;
  }
};
